import { Megaphone, Search, MapPinned, Linkedin, MessageCircle, Target, CheckCircle2, Share2, Youtube } from "lucide-react";
import PageHero, { ScrollCta } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";

const SERVICES = [
  {
    icon: Megaphone,
    title: "Meta Ads Management (Facebook & Instagram)",
    desc: "We deploy highly targeted Facebook and Instagram campaigns focused strictly on your objectives, whether that is lead generation, brand awareness, or direct conversions. From ad copy and creative design to continuous audience and budget optimization, we handle the entire campaign lifecycle.",
  },
  {
    icon: Search,
    title: "Google Ads & PPC (Pay-Per-Click)",
    desc: "Capture users at the exact moment they are actively searching for your services. We expertly manage Google Search Ads, Display Ads, and advanced Performance Max campaigns to drive immediate, measurable traffic to your conversion-focused landing pages.",
  },
  {
    icon: MapPinned,
    title: "Search Engine Optimization (SEO) & Local SEO",
    desc: "Build long-term, compounding organic visibility. Our comprehensive SEO services cover technical optimization, on-page keyword strategy, and strict Google Business Profile optimization to ensure you dominate local search rankings in your target market.",
  },
  {
    icon: Share2,
    title: "Social Media Marketing",
    desc: "Maintain a powerful, consistent organic presence. We provide end-to-end profile optimization, cover design, and monthly content scheduling — including static creatives, carousels, and highly informative Reels designed to engage and educate your audience.",
  },
  {
    icon: Linkedin,
    title: "LinkedIn B2B Marketing",
    desc: "Connect directly with corporate decision-makers. We optimize your LinkedIn company page and run targeted B2B lead generation campaigns focused specifically on business owners and top-tier professionals.",
  },
  {
    icon: Youtube,
    title: "YouTube & Video Marketing",
    desc: "Leverage the power of video to explain complex services and build trust. We manage complete YouTube channel optimization, video SEO, and awareness campaigns to position you as an industry authority.",
  },
  {
    icon: MessageCircle,
    title: "Email & WhatsApp Automation",
    desc: "Convert passive traffic into active business. We integrate the WhatsApp Business API for automated, instant enquiry responses and deploy professional email lead-nurturing campaigns to ensure your sales pipeline never goes cold.",
  },
];

const MKT_SERVICES = [
  "Meta Ads Management",
  "Google Ads & PPC",
  "SEO & Local SEO",
  "Social Media Marketing",
  "LinkedIn B2B Marketing",
  "YouTube & Video Marketing",
  "Email & WhatsApp Automation",
];

const LOOP = [
  { n: "01", t: "Audit", d: "We tear down your current funnel, spend and lead quality." },
  { n: "02", t: "Strategy", d: "Channel mix, budgets and offer — planned against a cost-per-lead target." },
  { n: "03", t: "Launch", d: "Campaigns, landing pages and CRM wiring go live together." },
  { n: "04", t: "Optimize", d: "Monthly reporting; spend follows the leads that convert." },
];

export default function DigitalMarketing() {
  return (
    <div data-testid="marketing-page">
      <PageHero
        eyebrow="B2B Growth Partner"
        title={["Digital", "Marketing."]}
        sub="At Evolve Elite, we don't just run ads; we build digital growth engines. Our data-driven marketing strategies are engineered to capture high-intent leads, elevate your brand authority, and maximize your return on ad spend (ROAS). We manage the entire ecosystem, ensuring your paid and organic channels work together seamlessly."
      >
        <ScrollCta href="#audit" label="Book a Marketing Audit" testId="marketing-audit-btn" />
      </PageHero>

      {/* SERVICES */}
      <section className="py-16 sm:py-24 lg:py-28" data-testid="marketing-services-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Core Services</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Seven channels. One objective: pipeline.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="card-lift h-full rounded-3xl border border-slate-200 bg-white p-7" data-testid={`marketing-service-${i + 1}`}>
                  <s.icon className="h-8 w-8 text-amber-600" />
                  <h3 className="mt-6 font-display text-xl font-extrabold text-navy">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3} className="lg:col-span-2">
              <div className="grain flex h-full flex-col justify-between rounded-3xl bg-navy p-7">
                <Target className="h-8 w-8 text-gold" />
                <div>
                  <h3 className="mt-8 font-display text-xl font-extrabold text-white">High-intent or nothing.</h3>
                  <p className="mt-2 max-w-xl text-sm text-white/65">
                    Paid and organic channels working together — every campaign judged on ROAS and cost per qualified enquiry, not vanity metrics.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* LOOP */}
      <section className="border-y border-slate-200 bg-[#F8FAFC] py-16 sm:py-20" data-testid="marketing-loop-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">The Operating Loop</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Audit. Strategy. Launch. Optimize.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {LOOP.map((l, i) => (
              <Reveal key={l.n} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-slate-200 bg-white p-7">
                  <span className="font-display text-4xl font-black text-gold/40">{l.n}</span>
                  <h3 className="mt-4 font-display text-lg font-extrabold text-navy">{l.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{l.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap gap-4">
              {["Monthly performance reports", "CRM-connected lead tracking", "Creative included"].map((pt) => (
                <span key={pt} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-navy">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" /> {pt}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* AUDIT FORM */}
      <section id="audit" className="py-16 sm:py-24" data-testid="marketing-audit-section">
        <div className="container-x grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">Free Audit</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Book a Marketing Audit.
            </h2>
            <p className="mt-5 max-w-md text-slate-600">
              A working session, not a sales call. We map where your leads leak, what your spend really buys and the
              three moves we'd make first — you keep the notes either way.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-8" data-testid="marketing-audit-form-card">
              <LeadForm
                source="marketing-page"
                fields={["name", "phone", "email", "service", "message"]}
                services={MKT_SERVICES}
                cta="Book a Marketing Audit"
                idPrefix="marketing-audit"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
